/**
 * device/app.ts — The App Interface
 *
 * Every app on the hPhone/hPad implements this interface. The overlay
 * renderer iterates the registry for the home screen grid and delegates
 * drawing/tapping to the active app.
 *
 * Apps draw in local coordinates — (0,0) is the top-left of their
 * content area. The overlay handles the device frame, status bar,
 * and navigation chrome.
 */

import type { GameState } from "../engine/state.js";

// ─── Geometry ─────────────────────────────────────────────────────────────

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ─── App Interface ────────────────────────────────────────────────────────

export interface DeviceApp {
  /** Unique identifier — used as key in appStates and notifications */
  id: string;

  /** Display name shown below the icon and in the app bar */
  name: string;

  /** Gradient colors for the icon background [top, bottom] */
  iconGradient: [string, string];

  /**
   * Draw the app icon. Called for each icon on the home screen.
   * cx/cy = center of the icon area, size = width/height of icon square.
   */
  drawIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void;

  /**
   * Draw the app content. rect is the available content area
   * (below the top bar, above the bottom safe area).
   * dt = time since last frame in seconds.
   */
  drawContent(ctx: CanvasRenderingContext2D, rect: Rect, state: GameState, dt: number): void;

  /**
   * Handle a tap at (localX, localY) relative to the content area origin.
   * Return true if the tap was consumed.
   */
  handleTap(localX: number, localY: number, state: GameState): boolean;

  /**
   * Handle a drag gesture. Called each frame while the user drags inside
   * the app content area. dy is the vertical delta since last frame.
   * Return true if the drag was consumed (prevents device frame drag).
   */
  handleDrag?(dy: number, state: GameState): void;

  /**
   * Called when a drag ends. Use for momentum / inertia scrolling.
   * velocityY is pixels-per-second at release.
   */
  handleDragEnd?(velocityY: number, state: GameState): void;

  /** Called when the app is opened */
  onOpen?(state: GameState): void;

  /** Called when the app is closed (navigating away or closing device) */
  onClose?(state: GameState): void;

  /**
   * Called when the user taps the back button. Return true if the app
   * handled it internally (e.g. going from detail → list view).
   * Return false or omit to let the overlay navigate to the home screen.
   */
  onBack?(state: GameState): boolean;

  /**
   * Called each frame with the pointer position (relative to content area).
   * Use for hover effects, tooltip display, etc.
   * x/y are -1,-1 when the pointer is outside the app content area.
   */
  handlePointerMove?(x: number, y: number, state: GameState): void;

  /** Return a badge count > 0 to show a notification dot on the icon */
  badge?(state: GameState): number;
}

// ─── App Registry ─────────────────────────────────────────────────────────

const _registry: DeviceApp[] = [];

/** Register a new app. Order of registration = order on home screen. */
export function registerApp(app: DeviceApp): void {
  _registry.push(app);
}

/** Get all registered apps */
export function getApps(): readonly DeviceApp[] {
  return _registry;
}

/** Find an app by ID */
export function getApp(id: string): DeviceApp | undefined {
  return _registry.find(a => a.id === id);
}

// ─── Shared UI Primitives ─────────────────────────────────────────────────

/** Draw a rounded rectangle (fill + optional stroke) */
export function drawRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  r: number, fillStyle?: string, strokeStyle?: string
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  if (fillStyle) {
    ctx.fillStyle = fillStyle;
    ctx.fill();
  }
  if (strokeStyle) {
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

/** Draw text with automatic truncation to fit a max width */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string, x: number, y: number,
  maxWidth: number, font: string, color: string,
  align: CanvasTextAlign = "left"
): void {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "top";

  let display = text;
  while (ctx.measureText(display).width > maxWidth && display.length > 1) {
    display = display.slice(0, -2) + "\u2026";
  }
  // Pixel-snap to prevent sub-pixel blur at small font sizes (6-9px range).
  // Fractional coordinates cause half-letter smearing on Canvas 2D text.
  ctx.fillText(display, Math.round(x), Math.round(y));
}

/** Draw a simple progress bar */
export function drawProgressBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  progress: number, fgColor: string, bgColor: string
): void {
  ctx.fillStyle = bgColor;
  drawRoundRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  if (progress > 0) {
    ctx.fillStyle = fgColor;
    drawRoundRect(ctx, x, y, Math.max(h, w * Math.min(1, progress)), h, h / 2);
    ctx.fill();
  }
}
